<script setup lang="ts">
import ChevronIcon from '@/assets/icons/shared/chevron.svg';
import ReducedBadge from '@/modules/ui/components/common/ReducedBadge.vue';
import type { I18TParams } from '@/i18n.ts';
import UiSpoiler from '@/modules/ui/components/ui/UiSpoiler.vue';
import UiTooltip from '@/modules/ui/components/ui/UiTooltip.vue';

const { label, error, invalid, open, stretch } = defineProps<{
  id: string;
  label?: string;
  error?: string | null | I18TParams;
  invalid?: boolean;
  required?: boolean;
  collapsible?: boolean;
  open?: boolean;
  count?: number;
  tooltip?: string;
  withTooltip?: boolean;
  tooltipDisabled?: boolean;
  stretch?: boolean;
}>();

defineEmits<{
  labelClick: [event: MouseEvent];
}>();
</script>

<template>
  <div
    class="ui-field"
    :class="{ _stretch: stretch }"
  >
    <label
      v-if="label"
      class="label"
      :class="{ _bold: required, _red: error || invalid, _collapsible: collapsible }"
      :for="id"
      @click="$emit('labelClick', $event)"
    >
      <ChevronIcon
        v-if="collapsible"
        v-rotate="{ isRotated: open, from: -90, to: 0 }"
        class="icon"
      />
      <span>{{ $maybeT(label) }}</span>
      <ReducedBadge
        v-if="count"
        :value="count"
      />
    </label>

    <UiSpoiler
      v-if="collapsible"
      :open="open"
    >
      <slot />
    </UiSpoiler>

    <UiTooltip
      v-else-if="withTooltip"
      :text="tooltip"
      placement="right"
      :disabled="tooltipDisabled"
    >
      <slot />
    </UiTooltip>

    <slot v-else />

    <Transition name="error">
      <span
        v-if="error"
        class="error"
      >
        {{ typeof error === 'string' ? $maybeT(error) : $t(error.key, error.params) }}
      </span>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.ui-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: var(--font-size-l);
  line-height: var(--line-height-l);

  &._stretch {
    :deep(.ui-tooltip) {
      display: block;
      width: 100%;
    }
  }
}

.label {
  display: flex;
  align-self: flex-start;
  margin: -8px -8px 0;
  padding: 8px;
  align-items: center;
  gap: 8px;
  color: var(--color-gray-600);
  transition: color var(--transition-fast);

  &._bold {
    font-weight: var(--font-weight-extra-bold);
  }

  &._red {
    color: var(--color-red-800);
  }

  &._collapsible {
    margin-top: -4px;
    cursor: pointer;
    font-size: var(--font-size-xl);
    line-height: var(--line-height-l);
    font-weight: var(--font-weight-bold);
  }
}

.icon {
  fill: var(--color-gray-700);
}

.error {
  color: var(--color-red-800);
  font-size: var(--font-size-s);
  line-height: var(--line-height-s);
  opacity: 1;
  height: 14px;
  transform: translateY(4px);

  &-enter-from,
  &-leave-to {
    height: 0;
    opacity: 0;
  }

  &-enter-active,
  &-leave-active {
    transition:
      height var(--transition),
      opacity var(--transition);
  }
}
</style>
